import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Settings, UserPlus, Image as ImageIcon, Pencil, Trash2, Camera, Send, Plus, Upload, Sparkles } from "lucide-react"
import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

// Données de démonstration
const projectMembers = [
  { id: 1, name: "Cedric2", avatar: "/images/team/cedric.png" }
]

const photoAlbums = [
  { 
    id: 1, 
    name: "Toutes les photos", 
    count: 6, 
    cover: "/lovable-uploads/dcc9872d-b20a-4f73-87a8-4e93a82d255c.png",
    editable: true
  },
  { 
    id: 2, 
    name: "Mes Favoris", 
    count: 0, 
    cover: "/lovable-uploads/752217e5-fca0-4155-87e4-90be7527e4e5.png",
    editable: true
  },
  { 
    id: 3, 
    name: "qsdfqs", 
    count: 3, 
    cover: "/lovable-uploads/96e635cc-7dfe-4a14-964c-7400fc06ff51.png",
    editable: true
  }
]

const photos = [
  { id: 1, url: "/lovable-uploads/0f0c0d83-1424-4804-83f5-3686120a05d4.png" },
  { id: 2, url: "/lovable-uploads/04cc4376-2a32-4819-865f-1bce96ab2c36.png" },
  { id: 3, url: "/lovable-uploads/1e280da6-cd56-420b-963e-a6a5d854ba9f.png" },
  { id: 4, url: "/lovable-uploads/6afc44f4-d7d1-468d-9337-3ed3f8b51507.png" },
  { id: 5, url: "/lovable-uploads/dcc9872d-b20a-4f73-87a8-4e93a82d255c.png" },
  { id: 6, url: "/lovable-uploads/1b9862f2-b8ce-4950-abef-78ddea86aa02.png" }
]

const chatMessages = [
  {
    id: 1,
    user: "Cedric2",
    avatar: "/images/team/cedric.png",
    images: [
      "/lovable-uploads/6afc44f4-d7d1-468d-9337-3ed3f8b51507.png",
      "/lovable-uploads/dcc9872d-b20a-4f73-87a8-4e93a82d255c.png",
      "/lovable-uploads/1b9862f2-b8ce-4950-abef-78ddea86aa02.png"
    ],
    date: "12/09/2025 à 13:19",
    text: "+ 3 photos dans Toutes les photos"
  }
]

export default function ProjectDetails() {
  const [message, setMessage] = useState("")
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcomeDialog(true)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Popup d'accueil pour ajouter des photos */}
      <Dialog open={showWelcomeDialog} onOpenChange={setShowWelcomeDialog}>
        <DialogContent className="sm:max-w-[600px] border-2 border-primary/20 bg-gradient-to-br from-background via-background to-primary/5">
          <DialogHeader>
            <DialogTitle className="text-3xl font-display text-center bg-gradient-to-r from-primary to-stats-orange bg-clip-text text-transparent mb-2">
              Partagez vos souvenirs !
            </DialogTitle>
            <DialogDescription className="text-center text-base text-muted-foreground">
              Commencez à créer votre album en ajoutant vos plus belles photos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-6">
            {/* Section principale - Ajouter des photos */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/10 via-stats-orange/10 to-primary/5 p-8 border-2 border-primary/30 hover:border-primary/50 transition-all cursor-pointer group">
              {/* Badge Recommandé */}
              <div className="absolute top-4 right-4 flex items-center gap-1 bg-gradient-to-r from-primary to-stats-orange text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                <Sparkles className="w-3 h-3" />
                Recommandé
              </div>

              <div className="flex flex-col items-center text-center space-y-6">
                {/* Icône principale */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary to-stats-orange rounded-full blur-xl opacity-30 animate-pulse"></div>
                  <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-primary to-stats-orange flex items-center justify-center shadow-elegant">
                    <Upload className="w-12 h-12 text-white" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-display font-bold text-foreground">
                    Ajouter vos photos
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    Téléchargez vos photos et commencez à construire votre album partagé avec vos proches
                  </p>
                </div>

                <Button 
                  variant="rainbow" 
                  size="lg" 
                  className="rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Ajouter mes photos
                </Button>
              </div>
            </div>

            {/* Section secondaire - Découvrir */}
            <div className="rounded-2xl bg-muted/30 p-6 border border-border hover:border-primary/30 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">
                    Explorer les albums existants
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Découvrez les photos déjà partagées par les membres
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={() => setShowWelcomeDialog(false)}
                >
                  Découvrir
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <main className="container mx-auto px-4 lg:px-8 py-8">
        {/* En-tête du projet */}
        <div className="flex items-start justify-between mb-8 mt-8">
          <div className="flex-1">
            <h1 className="text-4xl font-display font-bold mb-4 text-primary">qsdfqsdf</h1>
            
            {/* Membres */}
            <Card className="bg-card border-border rounded-2xl p-4 mb-6 inline-block">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <UserPlus className="w-4 h-4" />
                  <span>Membres</span>
                </div>
                <div className="flex -space-x-2">
                  {projectMembers.map((member) => (
                    <Avatar key={member.id} className="w-10 h-10 border-2 border-background">
                      <AvatarImage src={member.avatar} alt={member.name} />
                      <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="icon" className="rounded-full">
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="rainbow" className="rounded-full">
              <UserPlus className="w-4 h-4 mr-2" />
              Inviter
            </Button>
          </div>
        </div>

        {/* Onglets principaux */}
        <Tabs defaultValue="photos" className="w-full">
          <TabsList className="w-full grid grid-cols-2 mb-8 bg-muted/50 p-1 rounded-full h-auto">
            <TabsTrigger 
              value="chat" 
              className="rounded-full py-3 text-base data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-stats-orange data-[state=active]:text-white"
            >
              Échanger vos photos
            </TabsTrigger>
            <TabsTrigger 
              value="photos" 
              className="rounded-full py-3 text-base data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-stats-orange data-[state=active]:text-white"
            >
              Mes Photos
            </TabsTrigger>
          </TabsList>

          {/* Contenu: Chat */}
          <TabsContent value="chat" className="space-y-6">
            <div className="bg-gradient-to-r from-primary/10 to-stats-orange/10 rounded-3xl p-8 min-h-[500px] flex flex-col">
              {/* Messages */}
              <div className="flex-1 space-y-6 mb-6">
                {chatMessages.map((msg) => (
                  <div key={msg.id} className="flex items-start gap-4">
                    <Avatar className="w-12 h-12 ring-2 ring-primary/20">
                      <AvatarImage src={msg.avatar} alt={msg.user} />
                      <AvatarFallback>{msg.user.charAt(0)}</AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-foreground">{msg.user}</span>
                        <span className="text-xs text-muted-foreground">{msg.date}</span>
                      </div>
                      
                      {msg.images && (
                        <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 inline-block border-2 border-primary/20 mb-2">
                          <div className="flex gap-2 mb-2">
                            {msg.images.map((img, idx) => (
                              <div key={idx} className="w-24 h-24 rounded-xl overflow-hidden">
                                <img 
                                  src={img} 
                                  alt="Shared photo" 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                          <p className="text-sm text-primary font-medium">{msg.text}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Zone de saisie */}
              <div className="flex gap-3">
                <Button variant="outline" size="icon" className="rounded-full bg-white/80 hover:bg-white">
                  <ImageIcon className="w-5 h-5 text-primary" />
                </Button>
                <Textarea
                  placeholder="Votre message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="flex-1 min-h-[60px] rounded-2xl bg-white/80 border-2 border-border resize-none"
                />
                <Button variant="rainbow" size="lg" className="rounded-full px-8">
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Contenu: Photos */}
          <TabsContent value="photos" className="space-y-8">
            {/* Albums */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-display font-bold text-foreground flex items-center gap-2">
                  <Camera className="w-6 h-6 text-primary" />
                  Mon Album
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Bouton Ajouter un dossier */}
                <Card className="border-2 border-dashed border-primary/30 hover:border-primary rounded-3xl transition-all cursor-pointer group">
                  <CardContent className="p-8 flex flex-col items-center justify-center h-48">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Plus className="w-8 h-8 text-primary" />
                    </div>
                    <p className="text-center font-medium text-foreground">Ajouter un dossier</p>
                  </CardContent>
                </Card>

                {/* Albums existants */}
                {photoAlbums.map((album) => (
                  <Card key={album.id} className="rounded-3xl overflow-hidden border-2 border-primary/20 hover:shadow-elegant transition-all group">
                    <CardContent className="p-0">
                      <div className="relative h-48">
                        <img 
                          src={album.cover} 
                          alt={album.name} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {album.editable && (
                          <div className="absolute top-3 right-3 flex gap-2">
                            <Button size="icon" variant="outline" className="w-8 h-8 rounded-full bg-white/90 hover:bg-white">
                              <Camera className="w-4 h-4 text-primary" />
                            </Button>
                            <Button size="icon" variant="outline" className="w-8 h-8 rounded-full bg-white/90 hover:bg-white">
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="p-4 bg-gradient-to-br from-card to-accent">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground mb-1">{album.name}</h3>
                            <p className="text-sm text-muted-foreground">({album.count}) <Pencil className="w-3 h-3 inline" /></p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Photos */}
            <div>
              <div className="bg-gradient-to-r from-primary/10 to-stats-orange/10 rounded-3xl p-6 mb-6">
                <h3 className="text-xl font-display font-bold text-center text-primary">Photos</h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {/* Bouton Ajouter une photo */}
                <Card className="border-2 border-dashed border-primary/30 hover:border-primary rounded-3xl transition-all cursor-pointer group aspect-square">
                  <CardContent className="p-6 flex flex-col items-center justify-center h-full">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                      <Plus className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-center text-sm font-medium text-foreground">Ajouter une photo</p>
                  </CardContent>
                </Card>

                {/* Photos existantes */}
                {photos.map((photo) => (
                  <Card key={photo.id} className="rounded-3xl overflow-hidden border-2 border-border hover:border-primary hover:shadow-elegant transition-all group aspect-square">
                    <CardContent className="p-0 h-full relative">
                      <img 
                        src={photo.url} 
                        alt="Photo" 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer />
    </div>
  )
}
